<template>
  <div class="paypal-wrapper">
    <!-- Pay Later promotional message banner -->
    <!-- PayPal automatically shows/hides this based on buyer's country eligibility -->
    <div id="paypal-message-container" class="paypal-message"></div>

    <!-- PayPal + Pay Later stacked buttons rendered by JS SDK -->
    <div id="paypal-button-container"></div>

    <!-- Error display -->
    <div v-if="errorMsg" class="paypal-error">
      <i class="el-icon-warning"></i> {{ errorMsg }}
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="paypal-loading">
      Loading PayPal...
    </div>
  </div>
</template>

<script>
import { loadScript } from '@paypal/paypal-js'
import { buildPaypalCheckoutCreateParams } from '@/utils/paymentMethods'

export default {
  name: 'PaypalButton',

  props: {
    // Internal order ID (0 in booking flow before order is created)
    orderId: {
      type: [String, Number],
      required: true,
    },
    // Internal payment record ID
    paymentId: {
      type: [String, Number],
      default: 0,
    },
    // Optional async function that creates the local order before creating the PayPal order.
    // Used in booking flow (orderId=0). Must return { order_id, payment_id }.
    beforeCreate: {
      type: Function,
      default: null,
    },
    // PayPal client ID — passed from parent (read from payment method API response)
    clientId: {
      type: String,
      default: '',
    },
    // ISO currency code, e.g. USD, CAD, EUR — drives multi-currency support
    currency: {
      type: String,
      default: 'USD',
    },
    // Total amount to charge, e.g. "199.00"
    amount: {
      type: [String, Number],
      required: true,
    },
    // When true, clicking the PayPal button is blocked at the SDK level (onClick → actions.reject)
    disabled: {
      type: Boolean,
      default: false,
    },
    // Message to show when the button is clicked while disabled
    disabledMessage: {
      type: String,
      default: 'Please complete all required fields before proceeding.',
    },
  },

  emits: ['paymentSuccess', 'paymentFail', 'paymentCancel', 'disabledClick'],

  data() {
    return {
      loading: true,
      errorMsg: '',
      // Resolved after beforeCreate() is called in booking flow
      resolvedOrderId: 0,
      resolvedPaymentId: 0,
      paypalSdk: null,
    }
  },

  watch: {
    amount() {
      this.renderPayLaterMessages()
    },
    currency() {
      this.renderPayLaterMessages()
    },
    clientId() {
      this.renderPayLaterMessages()
    },
  },

  async mounted() {
    await this.initPaypal()
  },

  methods: {
    async initPaypal() {
      this.loading = true
      this.errorMsg = ''

      try {
        if (!this.clientId) {
          this.loading = false
          this.errorMsg = 'PayPal client ID is not configured.'
          return
        }

        // Load PayPal JS SDK with Pay Later and Venmo enabled
        // The SDK will automatically show Pay Later button if buyer is eligible
        const paypal = await loadScript({
          clientId:       this.clientId,
          currency:       this.currency,
          components:     'buttons,messages',   // load both checkout buttons and Pay Later messages
          enableFunding:  'paylater,venmo',     // show Pay Later and Venmo buttons when eligible
          disableFunding: 'card',               // hide card entry (already handled by Stripe)
        })

        this.paypalSdk = paypal
        this.loading = false

        await this.renderPayLaterMessages()

        // Render the stacked buttons: [PayPal] [Pay Later] [Venmo]
        paypal.Buttons({
          style: {
            layout: 'vertical',  // stacks all funding sources vertically
            color:  'blue',
            shape:  'rect',
            label:  'pay',       // shows "Pay with PayPal" text
          },

          // Fires synchronously when the button is clicked — before createOrder.
          // Calling actions.reject() prevents the PayPal popup from opening.
          // This is the official SDK way to conditionally block payment flow.
          onClick: (data, actions) => {
            if (this.disabled) {
              this.errorMsg = this.disabledMessage
              this.$emit('disabledClick')
              return actions.reject()
            }
            this.errorMsg = ''
            return actions.resolve()
          },

          // Step 1: Create a PayPal order on our PHP backend
          // This is called when the buyer clicks the PayPal button
          createOrder: async () => {
            this.errorMsg = ''

            // Run beforeCreate whenever it is provided:
            // - booking flow (orderId=0): creates the local order and returns {order_id, payment_id}
            // - pay-balance flow (orderId>0): validates terms + calls checkout/confirm, returns confirmed IDs
            let orderId   = parseInt(this.orderId) || this.resolvedOrderId
            let paymentId = parseInt(this.paymentId) || this.resolvedPaymentId
            let localPayment = {}

            if (typeof this.beforeCreate === 'function') {
              try {
                const local = await this.beforeCreate()
                localPayment = local
                orderId   = local.order_id   || orderId
                paymentId = local.payment_id || paymentId
                this.resolvedOrderId   = orderId
                this.resolvedPaymentId = paymentId
              } catch (e) {
                this.errorMsg = e.message || 'Failed to create order. Please try again.'
                throw e
              }
            }

            if (!orderId) {
              this.errorMsg = 'Order not ready. Please try again.'
              throw new Error(this.errorMsg)
            }

            const apiUrl = import.meta.env.VITE_API_URL

            const res = await fetch(
              `${apiUrl}payment/paypal_checkout/create_order&language_id=1`,
              {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(buildPaypalCheckoutCreateParams({
                  orderId,
                  paymentId,
                  currency: this.currency,
                  amount: this.amount,
                  local: localPayment,
                })),
              }
            )
            const data = await res.json()

            if (!data.paypal_order_id) {
              this.errorMsg = data.error || 'Failed to initialize PayPal payment.'
              throw new Error(this.errorMsg)
            }

            // Return the PayPal order ID to the JS SDK to open the popup
            return data.paypal_order_id
          },

          // Step 2: Capture the payment after buyer approves in PayPal popup
          onApprove: async (data) => {
            this.errorMsg = ''
            const apiUrl = import.meta.env.VITE_API_URL
            // Use resolved IDs (set during createOrder in booking flow)
            const orderId   = this.resolvedOrderId   || parseInt(this.orderId)   || 0
            const paymentId = this.resolvedPaymentId || parseInt(this.paymentId) || 0

            const res = await fetch(
              `${apiUrl}payment/paypal_checkout/capture_order&language_id=1`,
              {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    new URLSearchParams({
                  paypal_order_id: data.orderID,
                  order_id:        orderId,
                  payment_id:      paymentId,
                }),
              }
            )
            const result = await res.json()

            if (result.success) {
              this.$emit('paymentSuccess', result)
            } else {
              this.errorMsg = result.error || 'Payment capture failed. Please try again.'
              this.$emit('paymentFail', result)
            }
          },

          // Called when the buyer cancels in the PayPal popup
          onCancel: () => {
            this.errorMsg = 'Payment was cancelled.'
            this.$emit('paymentCancel')
          },

          // Called on unexpected SDK or network errors.
          // Do NOT overwrite errorMsg if it was already set by beforeCreate/onClick
          // (e.g. "Please agree to Terms") — only set it for genuine SDK errors.
          onError: (err) => {
            console.error('PayPal SDK error:', err)
            if (!this.errorMsg) {
              this.errorMsg = 'PayPal encountered an error. Please try again or use another payment method.'
            }
            this.$emit('paymentFail', { error: this.errorMsg, source: 'paypal_sdk' })
          },
        }).render('#paypal-button-container')

      } catch (err) {
        this.loading = false
        if (!this.errorMsg) {
          this.errorMsg = 'Failed to load PayPal. Please refresh the page.'
        }
        console.error('PaypalButton init error:', err)
      }
    },

    async renderPayLaterMessages() {
      const paypal = this.paypalSdk
      const amount = parseFloat(this.amount)

      if (!paypal || typeof paypal.Messages !== 'function' || !amount || amount <= 0) {
        return
      }

      await this.$nextTick()

      // Render a prominent flex-style Pay Later banner in the form area.
      // The external container is injected by the parent page/modal so it can sit near the payment method.
      const flexContainer = document.getElementById('paypal-paylater-flex-banner')
      if (flexContainer) {
        flexContainer.innerHTML = ''

        try {
          await paypal.Messages({
            amount,
            currency: this.currency,
            style: {
              layout: 'flex',
              ratio: '8x1',
              color: 'blue',
            },
          }).render('#paypal-paylater-flex-banner')
        } catch (e) {
          // Pay Later messaging not available for this placement/amount; keep checkout usable.
        }
      }

      // Render Pay Later text line near the PayPal button as a secondary/fallback message.
      const inlineContainer = document.getElementById('paypal-message-container')
      if (inlineContainer) {
        inlineContainer.innerHTML = ''

        try {
          await paypal.Messages({
            amount,
            currency: this.currency,
            placement: 'payment',
          }).render('#paypal-message-container')
        } catch (e) {
          // Pay Later messaging not available for this placement/amount; keep checkout usable.
        }
      }
    },
  },
}
</script>

<style scoped>
.paypal-wrapper {
  width: 100%;
  max-width: 400px;
}

.paypal-message {
  margin-bottom: 12px;
  min-height: 20px;
}

.paypal-error {
  margin-top: 8px;
  padding: 8px 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  color: #cf1322;
  font-size: 14px;
}

.paypal-loading {
  padding: 12px;
  color: #999;
  font-size: 14px;
  text-align: center;
}
</style>
